import { useMemo } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { VolumeChartProps } from 'Features/VolumeChart/VolumeChart.types'
import { format } from 'date-fns'

export const VolumeGraph = ({
  xAxisData,
  unconstrainedVolume,
  conEqUncon,
  conGrtUncon,
  conLtUncon,
}: VolumeChartProps) => {
  const hovertemplate = () => {
    return `%{text}<br>` + `<extra></extra>`
  }

  const colors = {
    conLtUnconBarColor: '#d467ac',
    conGrtUnconBarColor: '#00a2e0',
    scatterLineColor: '#838DC8',
    conEqUnconBarColor: '#d8d8d8', // Grey
  }

  const hoverTextCompiled: string[] | undefined | (number | undefined)[] =
    useMemo(() => {
      try {
        const constrainedIncreased = conGrtUncon?.map((data: number) => {
          if (data > 0) {
            return 'Constrained Volume Increased'
          }
          return ''
        })

        const constrainedDecreased = conLtUncon?.map((data: number) => {
          if (data > 0) {
            return 'Constrained Volume Decreased'
          }
          return ''
        })

        const volumeUnchanged = conEqUncon?.map((data: number) => {
          if (data > 0) {
            return 'Constrained Volume Unchanged'
          }
          return ''
        })

        return xAxisData?.map((dateText: string, index: number) => {
          const constrainedVolumeText =
            constrainedIncreased[index] ||
            constrainedDecreased[index] ||
            volumeUnchanged[index]

          const constrainedVolume =
            conGrtUncon[index] || conLtUncon[index] || conEqUncon[index]

          const unconstrainedVolumeValue = unconstrainedVolume[index].toFixed(1)

          const week = `${format(new Date(dateText), 'dd MMM yyy')} (Week ${
            index + 1
          })`
          return (
            `   <b>${constrainedVolumeText}</b><br>` +
            `   <b>Week:  </b>${week}<br>` +
            `   <b>Unconstrained Volume:  </b>${unconstrainedVolumeValue}<br>` +
            `   <b>Constrained Volume:  </b>${constrainedVolume.toFixed(
              1
            )}<br> `
          )
        })
      } catch (e) {
        console.log('error', e)
        return new Array(xAxisData?.length).fill('-')
      }
    }, [xAxisData, unconstrainedVolume, conEqUncon, conGrtUncon, conLtUncon])

  const trace1 = useMemo(() => {
    return {
      x: xAxisData,
      y: unconstrainedVolume,
      hovertemplate: hovertemplate(),
      text: hoverTextCompiled,
      textfont: { color: 'transparent' },
      mode: 'lines',
      type: 'scatter',
      name: 'Unconstrained Volume',
      line: {
        color: colors.scatterLineColor,
        width: 3,
        dash: 'dashdot',
      },
    }
  }, [
    xAxisData,
    unconstrainedVolume,
    colors.scatterLineColor,
    hoverTextCompiled,
  ])

  const trace2 = useMemo(() => {
    return {
      x: xAxisData,
      y: conEqUncon,
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      text: hoverTextCompiled,
      marker: {
        color: colors.conEqUnconBarColor,
        // Grey
      },
      name: 'Constrained Volume Unchanged',
      type: 'bar',
    }
  }, [xAxisData, conEqUncon, colors.conEqUnconBarColor, hoverTextCompiled])

  const trace3 = useMemo(() => {
    return {
      x: xAxisData,
      y: conGrtUncon,
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      text: hoverTextCompiled,
      marker: {
        color: colors.conGrtUnconBarColor,
        // Blue
      },
      name: 'Constrained Volume Increased',
      type: 'bar',
    }
  }, [xAxisData, conGrtUncon, colors.conGrtUnconBarColor, hoverTextCompiled])

  const trace4 = useMemo(() => {
    return {
      x: xAxisData,
      y: conLtUncon,
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      text: hoverTextCompiled,
      marker: {
        color: colors.conLtUnconBarColor,
        // Purple
      },
      name: 'Constrained Volume Decreased',
      type: 'bar',
    }
  }, [xAxisData, conLtUncon, colors.conLtUnconBarColor, hoverTextCompiled])

  const data = useMemo(
    () => [trace1, trace2, trace3, trace4],
    [trace1, trace2, trace3, trace4]
  )

  const layout = {
    barmode: 'stack',
    height: 300,
    yaxis: {
      showgrid: true,
      title: {
        text: 'Volume',
      },
    },
    xaxis: {
      ntick: 10,
      tickformat: '%d %b %y',
    },
    legend: { orientation: 'h', x: 0.1, y: -0.3 },
  }

  return <Graph data={data} layout={layout} />
}
